import {
  AlarmIcon,
  CameraIcon,
  Box as DarkBox,
  DeviceStatusIcon,
  DoorIcon,
  FeedItem,
  MapPinIcon,
} from '@hivewatch/dark'

import { DeviceStatus, DeviceType } from '@/graphql/generated/schemas'

import { darkDevicesPanelStyles } from './DarkDevicesPanel.styles'

interface DarkDeviceListItemIProps {
  colorScheme: 'dark' | 'light'
  deviceId: string
  deviceHealth: DeviceStatus
  deviceName?: string
  deviceType?: DeviceType
  placed: boolean
  selected: boolean
  onDeviceItemHoverIn?: (deviceId: string) => void
  onDeviceItemHoverOut?: () => void
}

const DeviceTypeIcon = ({ type }: { type?: DeviceType }) => {
  if (type === DeviceType.Alarm) return <AlarmIcon size={20} />
  if (type === DeviceType.Door) return <DoorIcon size={20} />
  return <CameraIcon size={20} />
}

// Health mapping mirrors the dark devices table (DeviceTableRow precedent).
const toStatusType = (status: DeviceStatus): 'online' | 'error' =>
  status === DeviceStatus.Healthy ? 'online' : 'error'

// The legacy row's camera-preview tooltip (DevicePopoverContent) is
// deliberately not mounted here — it returns with the dark DeviceHoverCard
// (map-layer phase, pending design specs).
export const DarkDeviceListItem = ({
  colorScheme,
  deviceId,
  deviceHealth,
  deviceName,
  deviceType,
  placed,
  selected,
  onDeviceItemHoverIn,
  onDeviceItemHoverOut,
}: DarkDeviceListItemIProps) => {
  const s = darkDevicesPanelStyles(colorScheme)

  return (
    <DarkBox
      data-testid='floorPlanPage_darkDevicePanelItem'
      onMouseEnter={() => onDeviceItemHoverIn?.(deviceId)}
      onMouseLeave={() => onDeviceItemHoverOut?.()}
      width='100%'
    >
      <FeedItem
        colorScheme={colorScheme}
        // Signals the affordance the way the legacy row did: a placed device is
        // clickable, an unplaced one is dragged onto the map. It goes on the
        // FeedItem rather than the wrapper because the row and its inner button
        // set their own cursor and would override anything set outside.
        cursor={placed ? 'pointer' : 'grab'}
        icon={<DeviceTypeIcon type={deviceType} />}
        isSelected={selected}
        message={deviceName || deviceId}
        trailingAction={
          <DarkBox {...s.trailingIcons}>
            {placed && (
              <DarkBox
                data-testid='floorPlanPage_darkDevicePanelItem_placed'
                display='inline-flex'
              >
                <MapPinIcon size={16} />
              </DarkBox>
            )}
            <DeviceStatusIcon
              colorScheme={colorScheme}
              status={toStatusType(deviceHealth)}
            />
          </DarkBox>
        }
      />
    </DarkBox>
  )
}
